import React, { type RefObject, useEffect } from 'react';

import type { PreloadedEntryPoint } from 'react-relay';

import type { ModalDialogProps } from '@atlaskit/modal-dialog/types';
import { getFirstFocusable } from '@atlaskit/top-layer/get-first-focusable';

import { WaitForEntryPointLoad } from '../wait-for-entry-point-load';

type FocusInsideOnLoadProps = {
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	entryPointReference: PreloadedEntryPoint<any>;
	autoFocusRef?: ModalDialogProps['autoFocus'];
	modalRef: RefObject<HTMLElement>;
};

function FocusInsideOnLoadInner({
	autoFocusRef,
	modalRef,
}: Omit<FocusInsideOnLoadProps, 'entryPointReference'>): null {
	useEffect(() => {
		// Fence for if container element doesn't exist, but this is effectively not
		// possible.
		if (!modalRef.current) {
			return;
		}
		// If ref provided, use that first. Else find first focusable element or
		// container if not exists
		if (autoFocusRef?.current) {
			autoFocusRef.current.focus();
			return;
		}

		const firstFocusable = getFirstFocusable({ container: modalRef.current });
		if (firstFocusable) {
			firstFocusable.focus();
			return;
		}

		modalRef.current.focus();
	}, [autoFocusRef, modalRef]);

	return null;
}

/**
 * Moves focus into the modal once the entry point has finished loading, so
 * makers don't have to manage focus manually after the loading fallback is
 * replaced by the final experience.
 *
 * If the modal was opened with an `autoFocus` ref, focus is moved to that
 * element. Otherwise, focus is moved to the first focusable element inside
 * the modal dialog, or the dialog itself.
 */
export function FocusInsideOnLoad({
	entryPointReference,
	autoFocusRef,
	modalRef,
}: FocusInsideOnLoadProps): React.JSX.Element {
	return (
		<WaitForEntryPointLoad entryPointReference={entryPointReference}>
			<FocusInsideOnLoadInner autoFocusRef={autoFocusRef} modalRef={modalRef} />
		</WaitForEntryPointLoad>
	);
}
